import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Row, Col, Container, Form } from "reactstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import CustomInput from "../../../components/AuthenticationComponents/CustomInput";
import CustomSelector from "../../../components/AuthenticationComponents/CustomSelector";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import DeleteConfirmationModal from "../../../components/Common/DeleteConfirmationModal";
import { get, post, deleteRequest } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import tickIcon from "../../../assets/images/support/tick-circle-white.svg";
import cancelIcon from "../../../assets/images/user-dashboard/remove-circle.svg";
import editIcon from "../../../assets/images/sidebar/setting-outline.svg";
import suspendIcon from "../../../assets/images/authentication/logout.svg";
import unlinkIcon from "../../../assets/images/authentication/credit-card.svg";
import resetIcon from "../../../assets/images/authentication/zap.svg";

/* -------------------------------------------------------------------------
 * AdminUserDetailPage — "Member Detail" admin view.
 *
 * Sections that already have a live API wired in:
 *   1. Member Information      <- endpoints.adminSingleUserRecord
 *   3. Dining Activity Overview (points side) + ledger table
 *                               <- endpoints.adminUserPointsLedger (incl. `summary`)
 *   5. Referral Summary         <- fields already on the user record
 *   8. Activity Timeline        <- endpoints.adminActivityLog
 *
 * Sections built as UI-complete placeholders, pending a backend endpoint
 * (marked with a `TODO(api)` comment wherever a real call needs dropping in):
 *   2. Member Linked Payment Account Details
 *   4. Voucher Overview
 *   6. Mini Games and Achievements
 *   7. Favorite Restaurants
 * ---------------------------------------------------------------------- */

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

const formatDateFromDDMMYY = (dateStr) => {
  if (!dateStr) return "N/A";
  // Parse DD/MM/YY format
  const parts = dateStr.split('/');
  if (parts.length !== 3) return dateStr;
  const [day, month, year] = parts;
  // Handle 2-digit year
  const fullYear = year.length === 2 ? `20${year}` : year;
  const date = new Date(fullYear, month - 1, day);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric"
  });
};

const isApiSuccess = (response) => {
  if (!response) return false;
  if (typeof response.data?.success !== "undefined") return response.data.success;
  if (typeof response.data?.status !== "undefined") return response.data.status;
  return response.status >= 200 && response.status < 300;
};

const extractPayload = (response) => {
  if (!response) return null;
  if (typeof response.data?.data !== "undefined") return response.data.data;
  return response.data || null;
};

/* ============ small shared bits ============ */

const StatCard = ({ label, value, accent, truncate }) => (
  <div className="rp-stat-card">
    <div className="rp-stat-card__top">
      <span className="rp-stat-card__label">{label}</span>
    </div>
    <div className="rp-stat-card__body">
      <div className="rp-stat-card__value-wrap au-stat-value-wrap">
        <span
          className={`rp-stat-card__value${truncate ? " au-stat-value--truncate" : ""}`}
          style={accent ? { color: accent } : undefined}
          title={truncate ? value : undefined}
        >
          {value}
        </span>
      </div>
    </div>
  </div>
);

const SectionCard = ({ title, subtitle, action, children }) => (
  <div className="rp-card au-section-card">
    <div className="rp-card__header">
      <div>
        <h2 className="rp-card__title au-section-title">{title}</h2>
        {subtitle && <p className="rp-card__subtitle">{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    active: "au-badge--green",
    redeemed: "au-badge--blue",
    expired: "au-badge--grey",
    pending: "au-badge--orange",
    completed: "au-badge--green",
    failed: "au-badge--red",
  };
  const key = (status || "").toLowerCase();
  return <span className={`au-badge ${map[key] || "au-badge--grey"}`}>{status || "N/A"}</span>;
};

const AdminUserDetailPage = () => {
  document.title = "Member Detail | Omaha Food Lovers";
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const initialUserData = location.state?.userData || null;

  const [userData, setUserData] = useState(initialUserData);
  const [loadingUser, setLoadingUser] = useState(!initialUserData);

  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  const [pointsLedger, setPointsLedger] = useState([]);
  const [ledgerSummary, setLedgerSummary] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerLoadingMore, setLedgerLoadingMore] = useState(false);
  const [ledgerHasMore, setLedgerHasMore] = useState(true);
  const [ledgerPage, setLedgerPage] = useState(1);

  const [vouchers, setVouchers] = useState([]);
  const [vouchersLoading, setVouchersLoading] = useState(false);

  const [memberGames, setMemberGames] = useState([]);
  const [memberGamesLoading, setMemberGamesLoading] = useState(false);
  const [memberGamesSummary, setMemberGamesSummary] = useState(null);

  const [wishlists, setWishlists] = useState([]);
  const [wishlistsLoading, setWishlistsLoading] = useState(false);

  const [referrals, setReferrals] = useState([]);
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [referralsSummary, setReferralsSummary] = useState(null);

  const [showPointsPanel, setShowPointsPanel] = useState(false);
  const [pointsAmount, setPointsAmount] = useState("");
  const [pointsReason, setPointsReason] = useState("");
  const [pointsAction, setPointsAction] = useState("add");
  const [pointsLoading, setPointsLoading] = useState(false);

  // Delete modal state for unlinking financial account
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);

  useEffect(() => {
    toastr.options = {
      positionClass: "toast-bottom-left",
      timeOut: 5000,
      closeButton: true,
      progressBar: true,
    };
  }, []);

  const fetchUserDetails = useCallback(async () => {
    try {
      setLoadingUser(true);
      const endpoint = endpoints.adminSingleUserRecord.replace("{{user_id}}", userId);
      const response = await get(endpoint);
      if (isApiSuccess(response)) {
        const payload = extractPayload(response);
        if (payload) setUserData(payload);
      }
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      toastr.error("Failed to load user details");
    } finally {
      setLoadingUser(false);
    }
  }, [userId]);

  const fetchActivityLogs = useCallback(async () => {
    try {
      setActivityLoading(true);
      const endpoint = `${endpoints.adminActivityLogs}?subject_type=App\\Models\\User&subject_id=${userId}&per_page=10&page=1`;
      const response = await get(endpoint);
      if (isApiSuccess(response)) {
        const payload = extractPayload(response);
        const logsCandidate = payload?.data ?? payload;
        if (Array.isArray(logsCandidate)) {
          setActivityLogs(logsCandidate);
        } else if (logsCandidate && typeof logsCandidate === "object") {
          setActivityLogs([logsCandidate]);
        } else {
          setActivityLogs([]);
        }
      } else {
        setActivityLogs([]);
      }
    } catch (error) {
      console.error("Failed to fetch activity logs:", error);
      setActivityLogs([]);
    } finally {
      setActivityLoading(false);
    }
  }, [userId]);

  const fetchPointsLedger = useCallback(async (page = 1, isLoadMore = false) => {
    try {
      if (isLoadMore) setLedgerLoadingMore(true);
      else setLedgerLoading(true);

      const endpoint = `${endpoints.adminUserPointsLedger.replace("{{user_id}}", userId)}?page=${page}&per_page=10`;
      const response = await get(endpoint);

      if (isApiSuccess(response)) {
        const paginationData = response.data?.data;

        if (paginationData) {
          if (paginationData.summary) setLedgerSummary(paginationData.summary);

          const rawRows = Array.isArray(paginationData.data) ? paginationData.data : [];
          const newLedger = rawRows.map((entry) => {
            const status = entry.status?.toLowerCase() || "credit";
            const points = Math.abs(Number(entry.points || 0));
            const type = entry.type || entry.type_key || "N/A";
            const transactionAmount = entry.transaction_amount ? `$${Number(entry.transaction_amount).toFixed(2)}` : "—";
            return {
              id: entry.id || `${entry.date_time || "ledger"}-${entry.transaction_id}`,
              transactionId: entry.transaction_id || "—",
              type,
              restaurant: entry.restaurant?.name || entry.restaurant_name || entry.meta?.restaurant_name || "—",
              transactionAmount,
              status,
              statusDisplay: status === "credit" ? "Credit" : "Debit",
              amountDisplay: `${status === "credit" ? "+" : "-"}${points}`,
              createdAt: entry.date_time ? formatDateTime(entry.date_time) : formatDateTime(entry.created_at),
            };
          });

          if (isLoadMore) setPointsLedger((prev) => [...prev, ...newLedger]);
          else setPointsLedger(newLedger);

          setLedgerPage(page);
          setLedgerHasMore(paginationData.current_page < paginationData.last_page);
        } else {
          setPointsLedger([]);
          setLedgerHasMore(false);
        }
      } else {
        setPointsLedger([]);
        setLedgerHasMore(false);
      }
    } catch (error) {
      console.error("Failed to fetch points ledger:", error);
      setPointsLedger([]);
      setLedgerHasMore(false);
    } finally {
      setLedgerLoading(false);
      setLedgerLoadingMore(false);
    }
  }, [userId]);

  const fetchVouchers = useCallback(async () => {
    try {
      setVouchersLoading(true);
      const endpoint = `${endpoints.adminUserVouchers}?customer_id=${userId}&per_page=10`;
      const response = await get(endpoint);
      if (isApiSuccess(response)) {
        const paginationData = response.data?.data;
        if (paginationData) {
          const rawVouchers = Array.isArray(paginationData.data) ? paginationData.data : [];
          const newVouchers = rawVouchers.map((v) => ({
            id: v.id,
            code: v.code,
            status: v.display_status || v.status,
            points: v.points,
            amount: v.amount,
            createdAt: formatDateTime(v.created_at),
            expiresAt: formatDateTime(v.expires_at),
            redeemedAt: v.redeemed_at ? formatDateTime(v.redeemed_at) : "—",
            restaurant: v.restaurant?.title || "N/A",
            reward: v.reward?.name || "N/A",
            customerEmail: v.customer?.email || "N/A",
          }));
          setVouchers(newVouchers);
        }
      }
    } catch (error) {
      console.error("Failed to fetch vouchers:", error);
      setVouchers([]);
    } finally {
      setVouchersLoading(false);
    }
  }, [userId]);

  const fetchMemberGames = useCallback(async () => {
    try {
      setMemberGamesLoading(true);
      const endpoint = `${endpoints.adminMemberGames}?customer_id=${userId}&per_page=10`;
      const response = await get(endpoint);
      if (isApiSuccess(response)) {
        const paginationData = response.data?.data;
        if (paginationData) {
          if (paginationData.summary) setMemberGamesSummary(paginationData.summary);
          const rawGames = Array.isArray(paginationData.data) ? paginationData.data : [];
          const newGames = rawGames.map((g) => ({
            id: g.id,
            name: g.name,
            type: g.type,
            status: g.status,
            progress: g.progress,
            pointsEarned: g.points_earned,
            startedAt: formatDateTime(g.started_at),
            lastProgressedAt: formatDateTime(g.last_progressed_at),
            completedAt: g.completed_at ? formatDateTime(g.completed_at) : "—",
          }));
          setMemberGames(newGames);
        }
      }
    } catch (error) {
      console.error("Failed to fetch member games:", error);
      setMemberGames([]);
    } finally {
      setMemberGamesLoading(false);
    }
  }, [userId]);

  const fetchWishlists = useCallback(async () => {
    try {
      setWishlistsLoading(true);
      const endpoint = `${endpoints.adminWishlists}?customer_id=${userId}&per_page=10`;
      const response = await get(endpoint);
      if (isApiSuccess(response)) {
        const paginationData = response.data?.data;
        if (paginationData) {
          const rawWishlists = Array.isArray(paginationData.data) ? paginationData.data : [];
          const newWishlists = rawWishlists.map((w) => ({
            id: w.id,
            restaurant: w.restaurant?.name || w.restaurant?.title || "N/A",
            address: w.restaurant?.address || "N/A",
            city: w.restaurant?.city || "N/A",
            favoritedAt: formatDateTime(w.favorited_at),
          }));
          setWishlists(newWishlists);
        }
      }
    } catch (error) {
      console.error("Failed to fetch wishlists:", error);
      setWishlists([]);
    } finally {
      setWishlistsLoading(false);
    }
  }, [userId]);

  const fetchReferrals = useCallback(async () => {
    try {
      setReferralsLoading(true);
      const endpoint = `${endpoints.adminReferrals}?referrer_user_id=${userId}&per_page=10`;
      const response = await get(endpoint);
      if (isApiSuccess(response)) {
        const paginationData = response.data?.data;
        if (paginationData) {
          if (paginationData.summary) setReferralsSummary(paginationData.summary);
          const rawReferrals = Array.isArray(paginationData.data) ? paginationData.data : [];
          const newReferrals = rawReferrals.map((r) => ({
            id: r.id,
            name: r.referred_member?.name || r.name || "N/A",
            email: r.referred_member?.email || r.email || "N/A",
            referralCode: r.referral_code || "N/A",
            status: r.display_status || r.status || "N/A",
            accountLinked: r.account_linked ? "Yes" : "No",
            pointsAwarded: r.points_awarded || 0,
            joinedAt: formatDateTime(r.joined_at),
            completedAt: r.completed_at ? formatDateTime(r.completed_at) : "—",
            rewardedAt: r.rewarded_at ? formatDateTime(r.rewarded_at) : "—",
          }));
          setReferrals(newReferrals);
        }
      }
    } catch (error) {
      console.error("Failed to fetch referrals:", error);
      setReferrals([]);
    } finally {
      setReferralsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUserDetails();
    fetchActivityLogs();
    fetchPointsLedger();
    fetchVouchers();
    fetchMemberGames();
    fetchWishlists();
    fetchReferrals();
  }, [fetchUserDetails, fetchActivityLogs, fetchPointsLedger, fetchVouchers, fetchMemberGames, fetchWishlists, fetchReferrals]);

  /* ---- Points add/deduct ---- */

  const openPointsPanel = (action) => {
    setPointsAction(action);
    setShowPointsPanel(true);
  };

  const handlePointsSubmit = async (event) => {
    event.preventDefault();
    if (!pointsAmount || !pointsReason) {
      toastr.warning("Please fill in all fields");
      return;
    }
    const parsedAmount = Math.abs(parseInt(pointsAmount, 10));
    if (!parsedAmount) {
      toastr.warning("Enter a valid amount");
      return;
    }
    try {
      setPointsLoading(true);
      const endpoint = endpoints.adminManageUserPoints.replace("{{user_id}}", userId);
      const response = await post(endpoint, {
        amount: parsedAmount,
        reason: pointsReason,
        action: pointsAction,
      });
      if (isApiSuccess(response)) {
        toastr.success(`Points ${pointsAction === "add" ? "added" : "subtracted"} successfully`);
        setPointsAmount("");
        setPointsReason("");
        setShowPointsPanel(false);
        setLedgerPage(1);
        setLedgerHasMore(true);
        fetchPointsLedger(1, false);
        fetchUserDetails();
      } else {
        toastr.error("Failed to update points");
      }
    } catch (error) {
      console.error("Failed to manage points:", error);
      toastr.error("Failed to update points");
    } finally {
      setPointsLoading(false);
    }
  };

  const handleLedgerLoadMore = () => {
    if (!ledgerLoadingMore && ledgerHasMore) fetchPointsLedger(ledgerPage + 1, true);
  };

  /* ---- Admin actions (some pending real endpoints — see TODO(api)) ---- */

  const handleUnlinkCardClick = () => {
    if (primaryAccount?.id) {
      setAccountToDelete(primaryAccount.id);
      setIsDeleteModalOpen(true);
    }
  };

  const handleUnlinkCardConfirm = async () => {
    if (!accountToDelete) return;

    try {
      const deleteEndpoint = endpoints.adminDeleteCustomerFinancialAccount.replace('{{financial_account_id}}', accountToDelete);
      await deleteRequest(deleteEndpoint);

      toastr.success("Financial account unlinked successfully!");
      setIsDeleteModalOpen(false);
      setAccountToDelete(null);
      fetchUserDetails();
    } catch (error) {
      console.error("Unlink card error:", error);
      console.error("Error response:", error.response?.data);
      const errorMessage = error.response?.data?.message || error.response?.data?.data?.stripe_error || "Failed to unlink financial account";
      toastr.error(errorMessage);
    }
  };

  const handleUnlinkCardCancel = () => {
    setIsDeleteModalOpen(false);
    setAccountToDelete(null);
  };

  /* ---- derived data ---- */

  const pointsLedgerColumns = useMemo(() => [
    { key: "transactionId", label: "Transaction ID", width: "1fr" },
    { key: "type", label: "Type", width: "1fr" },
    { key: "restaurant", label: "Restaurant", width: "1.5fr" },
    {
      key: "transactionAmount",
      label: "Amount",
      width: "1fr",
      align: "right",
      render: (row) => row.transactionAmount || "—",
    },
    {
      key: "amountDisplay",
      label: "Points",
      width: "0.8fr",
      align: "right",
      render: (row) => (
        <span className={row.status === "credit" ? "nt-points" : "au-badge--red-text"}>{row.amountDisplay}</span>
      ),
    },
    {
      key: "status",
      label: "Status",
      width: "0.8fr",
      render: (row) => (
        <span className={row.status === "credit" ? "nt-points" : "au-badge--red-text"}>{row.statusDisplay}</span>
      ),
    },
    { key: "createdAt", label: "Date & Time", width: "1.2fr" },
  ], []);

  const activityRows = useMemo(
    () =>
      activityLogs
        .filter(Boolean)
        .map((log, index) => ({
          id: log.id || `${log.date || "activity"}-${index}`,
          createdAt: log.date || log.created_at || "N/A",
          logName: log.event_label || log.type || log.event || "N/A",
          description: (log.description || "N/A").replace(/<[^>]*>?/gm, ""),
          performedBy: log.performed_by || "N/A",
          changes: log.changes || [],
        })),
    [activityLogs]
  );

  const referralRows = useMemo(
    () =>
      referrals.map((r) => ({
        id: r.id,
        name: r.name,
        email: r.email,
        referralCode: r.referralCode,
        status: r.status,
        accountLinked: r.accountLinked,
        points: r.pointsAwarded,
        joinedAt: r.joinedAt,
        completedAt: r.completedAt,
        rewardedAt: r.rewardedAt,
      })),
    [referrals]
  );

  const referralColumns = useMemo(() => [
    { key: "name", label: "User Name", width: "1.2fr" },
    { key: "email", label: "Email", width: "1.4fr" },
    { key: "referralCode", label: "Referral Code", width: "1.0fr" },
    { key: "status", label: "Status", width: "0.8fr", render: (r) => <StatusBadge status={r.status} /> },
    { key: "accountLinked", label: "Account Linked", width: "0.9fr", align: "center" },
    { key: "points", label: "Points", width: "0.7fr", align: "right" },
    { key: "joinedAt", label: "Joined", width: "1.0fr" },
    { key: "completedAt", label: "Completed", width: "1.0fr" },
    { key: "rewardedAt", label: "Rewarded", width: "1.0fr" },
  ], []);

  const voucherColumns = useMemo(() => [
    { key: "code", label: "Code", width: "1.0fr" },
    { key: "restaurant", label: "Restaurant", width: "1.2fr" },
    { key: "reward", label: "Reward", width: "1.0fr" },
    { key: "status", label: "Status", width: "0.8fr", render: (r) => <StatusBadge status={r.status} /> },
    { key: "amount", label: "Amount", width: "0.7fr", align: "right", render: (r) => `$${r.amount?.toFixed(2) || "0.00"}` },
    { key: "points", label: "Points", width: "0.7fr", align: "right" },
    { key: "customerEmail", label: "Customer Email", width: "1.4fr" },
    { key: "createdAt", label: "Created", width: "1.0fr" },
    { key: "expiresAt", label: "Expires", width: "1.0fr" },
    { key: "redeemedAt", label: "Redeemed", width: "1.0fr" },
  ], []);

  const gameColumns = useMemo(() => [
    { key: "name", label: "Game Name", width: "1.4fr" },
    { key: "type", label: "Type", width: "0.8fr" },
    { key: "status", label: "Status", width: "0.8fr", render: (r) => <StatusBadge status={r.status} /> },
    { key: "progress", label: "Progress", width: "1.0fr", render: (r) => `${r.progress?.current || 0}/${r.progress?.required || 0}` },
    { key: "percentage", label: "%", width: "0.6fr", align: "right", render: (r) => `${r.progress?.percentage || 0}%` },
    { key: "pointsEarned", label: "Points Earned", width: "0.8fr", align: "right" },
    { key: "startedAt", label: "Started", width: "1.0fr" },
    { key: "lastProgressedAt", label: "Last Progress", width: "1.0fr" },
    { key: "completedAt", label: "Completed", width: "1.0fr" },
  ], []);

  const wishlistColumns = useMemo(() => [
    { key: "restaurant", label: "Restaurant", width: "1.4fr" },
    { key: "address", label: "Address", width: "1.8fr" },
    { key: "city", label: "City", width: "0.8fr" },
    { key: "favoritedAt", label: "Favorited", width: "1.0fr" },
  ], []);

  const financialAccounts = userData?.account_info?.financial_accounts || [];
  const hasPaymentAccount = financialAccounts.length > 0;
  const primaryAccount = hasPaymentAccount ? financialAccounts[0] : null;

  const memberId = userData?.id ? `MEM-${String(userData.id).padStart(6, "0")}` : "—";

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page au-page">
          {/* ---- Top bar ---- */}
          <div className="au-topbar">
            <p className="au-breadcrumb">
              <span className="au-breadcrumb__link" onClick={() => navigate("/admin/users")}>Members</span>
              <span className="au-breadcrumb__sep">›</span>
              <span className="au-breadcrumb__current">Member Detail</span>
            </p>
            <div className="au-topbar__actions">
              <SecondaryButton
                variant="light-orange"
                icon={editIcon}
                onClick={() => navigate(`/admin/users/${userId}/edit`, { state: { userData } })}
              >
                Edit Member
              </SecondaryButton>
              <SecondaryButton
                variant="light-orange"
                icon={suspendIcon}
                onClick={() => navigate(`/admin/users/${userId}/edit`, { state: { userData } })}
              >
                {userData?.is_active ? "Suspend Member" : "Reactivate Member"}
              </SecondaryButton>
            </div>
          </div>

          {loadingUser && !userData ? (
            <div className="au-loading">
              <div className="rp-skeleton" style={{ width: 260, height: 32, marginBottom: 20 }} />
              <div className="rp-skeleton" style={{ width: "100%", height: 120 }} />
            </div>
          ) : (
            <>
              {/* ---- 1. Member Information ---- */}
              <SectionCard title="Member Information" subtitle="View and manage member details">
                <div className="au-member-row">
                  <div className="au-avatar">
                    {userData?.avatar || userData?.logo ? (
                      <img src={userData.avatar || userData.logo} alt={userData?.full_name} />
                    ) : (
                      <span>
                        {(userData?.full_name || "MU")
                          .split(" ")
                          .filter(Boolean)
                          .slice(0, 2)
                          .map((c) => c[0])
                          .join("")
                          .toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="au-detail-grid">
                    <div className="au-detail-item">
                      <span className="au-detail-item__label">Member ID</span>
                      <span className="au-detail-item__value">{memberId}</span>
                    </div>
                    <div className="au-detail-item">
                      <span className="au-detail-item__label">Full Name &amp; Email</span>
                      <span className="au-detail-item__value">{userData?.full_name || "N/A"}</span>
                      <span className="au-detail-item__sub">{userData?.email || "N/A"}</span>
                    </div>
                    <div className="au-detail-item">
                      <span className="au-detail-item__label">Date Joined</span>
                      <span className="au-detail-item__value">{userData?.created_at_formatted || formatDateTime(userData?.created_at)}</span>
                    </div>
                    <div className="au-detail-item">
                      <span className="au-detail-item__label">Member Since</span>
                      <span className="au-detail-item__value">{formatDateFromDDMMYY(userData?.date_register)}</span>
                    </div>
                    <div className="au-detail-item">
                      <span className="au-detail-item__label">Status</span>
                      <span>
                        <StatusBadge status={userData?.is_active ? "Active" : "Inactive"} />
                      </span>
                    </div>
                  </div>
                </div>
              </SectionCard>

              {/* ---- 2. Linked Payment Account (pending confirmed schema) ---- */}
              <SectionCard title="Account Detail" subtitle="Manage linked payment accounts">
                {hasPaymentAccount ? (
                  <div className="au-payment-row">
                    <div className="au-payment-brand">{primaryAccount.display_name || primaryAccount.institution_name || "Card"}</div>
                    <div className="au-detail-grid">
                      <div className="au-detail-item">
                        <span className="au-detail-item__label">Account Number</span>
                        <span className="au-detail-item__value">•••• •••• •••• {primaryAccount.last4 || "····"}</span>
                      </div>
                      <div className="au-detail-item">
                        <span className="au-detail-item__label">Bank Name</span>
                        <span className="au-detail-item__value">{primaryAccount.institution_name || "N/A"}</span>
                      </div>
                      <div className="au-detail-item">
                        <span className="au-detail-item__label">Category</span>
                        <span className="au-detail-item__value">{primaryAccount.category || "N/A"}</span>
                      </div>
                      <div className="au-detail-item">
                        <span className="au-detail-item__label">Subcategory</span>
                        <span className="au-detail-item__value">{primaryAccount.subcategory || "N/A"}</span>
                      </div>
                      <div className="au-detail-item">
                        <span className="au-detail-item__label">Connection Status</span>
                        <span className="au-detail-item__value au-connected"><i className="au-dot au-dot--green" /> {primaryAccount.status || "Connected"}</span>
                      </div>
                      <div className="au-detail-item">
                        <span className="au-detail-item__label">Linked On</span>
                        <span className="au-detail-item__value">{formatDateTime(primaryAccount.created_at)}</span>
                      </div>
                    </div>
                    <SecondaryButton variant="light-orange" icon={unlinkIcon} className="au-unlink-btn" onClick={handleUnlinkCardClick}>Unlink Card</SecondaryButton>
                  </div>
                ) : (
                  <div className="au-pending">
                    <div className="au-pending__icon">💳</div>
                    <p className="au-pending__text">
                      {userData?.linked_cards_count > 0
                        ? "This member has a linked card, but account details are pending API integration."
                        : "No payment account linked for this member yet."}
                    </p>
                  </div>
                )}
              </SectionCard>

              {/* ---- 3. Dining Activity Overview ---- */}
              <SectionCard title="Points Ledger" subtitle="Track member points and transactions">
                  <div className="au-stats-grid--4">
                    <StatCard label="Total Points Earned" value={(ledgerSummary?.earned_points ?? userData?.total_points_earned ?? 0).toLocaleString()} accent="#1ba97f" />
                    <StatCard label="Available Points" value={(userData?.points_balance ?? 0).toLocaleString()} />
                    <StatCard label="Total Points Redeemed" value={(ledgerSummary?.redeemed_points ?? userData?.total_points_redeemed ?? 0).toLocaleString()} />
                    <StatCard label="Total Transactions" value={(ledgerSummary?.total_transactions ?? 0).toLocaleString()} />
                  </div>
                  <DataTable
                    columns={pointsLedgerColumns}
                    rows={pointsLedger}
                    getRowKey={(row) => row.id}
                    loading={ledgerLoading}
                    loadingMore={ledgerLoadingMore}
                    onLoadMore={handleLedgerLoadMore}
                    hasMore={ledgerHasMore}
                    compact={true}
                  />
                </SectionCard>

              {/* ---- 4. Voucher Overview ---- */}
              <SectionCard title="Voucher Overview" subtitle="View member vouchers and redemptions">
                <div className="au-stats-grid au-stats-grid--4">
                  <StatCard label="Total Vouchers" value={vouchers.length.toLocaleString()} />
                  <StatCard label="Active" value={vouchers.filter(v => v.status?.toLowerCase() === "active").length.toLocaleString()} accent="#1ba97f" />
                  <StatCard label="Redeemed" value={vouchers.filter(v => v.status?.toLowerCase() === "redeemed").length.toLocaleString()} accent="#3b82f6" />
                  <StatCard label="Expired" value={vouchers.filter(v => v.status?.toLowerCase() === "expired").length.toLocaleString()} accent="#6b7280" />
                </div>
                <DataTable
                  columns={voucherColumns}
                  rows={vouchers}
                  getRowKey={(row) => row.id}
                  loading={vouchersLoading}
                  compact={true}
                />
              </SectionCard>

              {/* ---- 5. Referral Summary ---- */}
              <SectionCard title="Referral Summary" subtitle="Track member referrals and rewards">
                <div className="au-stats-grid au-stats-grid--5">
                  <StatCard label="Referral Code" value={userData?.referral_code || "—"} truncate />
                  <StatCard label="Total Referrals" value={(referralsSummary?.total_referrals ?? 0).toLocaleString()} />
                  <StatCard label="Pending" value={(referralsSummary?.pending ?? 0).toLocaleString()} accent="#f59e0b" />
                  <StatCard label="Completed" value={(referralsSummary?.completed ?? 0).toLocaleString()} accent="#1ba97f" />
                  <StatCard label="Points Earned" value={(referralsSummary?.total_points_earned ?? 0).toLocaleString()} accent="#3b82f6" />
                </div>
                <DataTable
                  columns={referralColumns}
                  rows={referralRows}
                  getRowKey={(row) => row.id}
                  loading={referralsLoading}
                  compact={true}
                />
              </SectionCard>

              {/* ---- 6. Mini Games and Achievements ---- */}
              <SectionCard title="Mini Games and Achievements" subtitle="View member game participation and achievements">
                <div className="au-stats-grid au-stats-grid--4">
                  <StatCard label="Total Games" value={memberGames.length.toLocaleString()} />
                  <StatCard label="Active" value={memberGames.filter(g => g.status?.toLowerCase() === "active").length.toLocaleString()} accent="#1ba97f" />
                  <StatCard label="Completed" value={memberGames.filter(g => g.status?.toLowerCase() === "completed").length.toLocaleString()} accent="#3b82f6" />
                  <StatCard label="Total Points Earned" value={(memberGamesSummary?.total_points_earned ?? 0).toLocaleString()} accent="#f59e0b" />
                </div>
                <DataTable
                  columns={gameColumns}
                  rows={memberGames}
                  getRowKey={(row) => row.id}
                  loading={memberGamesLoading}
                  compact={true}
                />
              </SectionCard>

              {/* ---- 7. Favorite Restaurants ---- */}
              <SectionCard title="Favorite Restaurants" subtitle="View member's favorite restaurants">
                <div className="au-stats-grid">
                  <StatCard label="Total Favorites" value={wishlists.length.toLocaleString()} />
                </div>
                <DataTable
                  columns={wishlistColumns}
                  rows={wishlists}
                  getRowKey={(row) => row.id}
                  loading={wishlistsLoading}
                  compact={true}
                />
              </SectionCard>

              {/* ---- 8. Activity Timeline ---- */}
              <SectionCard title="Activity Timeline" subtitle="View member activity history">
                {activityLoading ? (
                  <div className="rp-skeleton" style={{ height: 100 }} />
                ) : activityRows.length === 0 ? (
                  <div className="au-pending">
                    <div className="au-pending__icon">📋</div>
                    <p className="au-pending__text">No activity recorded yet.</p>
                  </div>
                ) : (
                  <div className="au-timeline" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {activityRows.map((row) => (
                      <div key={row.id} className="au-timeline__item">
                        <span className="au-timeline__dot" />
                        <div className="au-timeline__content">
                          <span className="au-timeline__date">{row.createdAt}</span>
                          <span className="au-timeline__desc">{row.description}</span>
                          <span className="au-timeline__meta">{row.logName} • {row.performedBy}</span>
                          {row.changes && row.changes.length > 0 && (
                            <div className="au-timeline__changes">
                              {row.changes.map((change, idx) => (
                                <div key={idx} className="au-timeline__change">
                                  <span>{change.field}: </span>
                                  <span className="au-timeline__change-old">{change.old_value}</span>
                                  <span> → </span>
                                  <span className="au-timeline__change-new">{change.new_value}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>

              {/* ---- 9. Admin Actions ---- */}
              <SectionCard title="Admin Actions" subtitle="Perform administrative actions on this account">
                <div className="au-action-grid">
                  <SecondaryButton variant="light-orange" icon={tickIcon} onClick={() => openPointsPanel("add")}>
                    Add Points
                  </SecondaryButton>
                  <SecondaryButton variant="light-orange" icon={cancelIcon} onClick={() => openPointsPanel("subtract")}>
                    Deduct Points
                  </SecondaryButton>
                  <SecondaryButton variant="light-orange" icon={resetIcon} onClick={() => navigate(`/admin/users/${userId}/edit`, { state: { userData } })}>
                    Reset Password
                  </SecondaryButton>
                  <SecondaryButton
                    variant="light-orange"
                    icon={suspendIcon}
                    onClick={() => navigate(`/admin/users/${userId}/edit`, { state: { userData } })}
                  >
                    {userData?.is_active ? "Suspend Member" : "Reactivate Member"}
                  </SecondaryButton>
                </div>

                {showPointsPanel && (
                  <div className="au-points-panel">
                    <Form onSubmit={handlePointsSubmit}>
                      <Row className="g-3">
                        <Col lg={3} md={6} sm={12}>
                          <CustomInput
                            id="pointsAmount"
                            name="pointsAmount"
                            label="Points Amount"
                            type="number"
                            min="1"
                            placeholder="Enter amount"
                            value={pointsAmount}
                            onChange={(event) => setPointsAmount(event.target.value)}
                            className="white-bg"
                          />
                        </Col>
                        <Col lg={3} md={6} sm={12}>
                          <CustomSelector
                            id="pointsAction"
                            name="pointsAction"
                            label="Action"
                            placeholder="Select action"
                            options={[
                              { value: "add", label: "Add Points" },
                              { value: "subtract", label: "Subtract Points" },
                            ]}
                            value={pointsAction}
                            onChange={(value) => setPointsAction(value)}
                            className="white-bg"
                          />
                        </Col>
                        <Col lg={4} md={6} sm={12}>
                          <CustomInput
                            id="pointsReason"
                            name="pointsReason"
                            label="Reason"
                            type="text"
                            placeholder="Enter reason"
                            value={pointsReason}
                            onChange={(event) => setPointsReason(event.target.value)}
                            className="white-bg"
                          />
                        </Col>
                        <Col lg={12} md={12} sm={12} className="d-flex justify-content-start gap-2 mt-3">
                          <SecondaryButton
                            type="submit"
                            variant="orange"
                            disabled={pointsLoading}
                            loading={pointsLoading}
                            icon={tickIcon}
                            iconPosition="right"
                          >
                            Save
                          </SecondaryButton>
                          <SecondaryButton
                            type="button"
                            variant="light-grey"
                            onClick={() => setShowPointsPanel(false)}
                            icon={cancelIcon}
                            iconPosition="right"
                          >
                            Cancel
                          </SecondaryButton>
                        </Col>
                      </Row>
                    </Form>
                  </div>
                )}
              </SectionCard>
            </>
          )}
        </div>
      </Container>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        title="Unlink Financial Account"
        message="Are you sure you want to unlink this financial account? This action cannot be undone."
        itemName="financial account"
        onCancel={handleUnlinkCardCancel}
        onConfirm={handleUnlinkCardConfirm}
      />
    </div>
  );
};

export default AdminUserDetailPage;